import type { FC } from 'react'
import { useState, useRef } from 'react'

import { AuthClient } from '@skytech/auth'
import { config } from '@skytech/config'
import { getEnvironmentBadgeLabel } from '@skytech/config/environmentBadge'
import { Select, Toggle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Image } from '@skytech/ui/Image'
import { availableLanguages } from '@skytech/ui/LanguageChoiceButtons'
import { WHITE_LABEL_LIST } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import AccountIcon from 'mdi-react/AccountIcon'
import BellIcon from 'mdi-react/BellIcon'
import ChevronUpIcon from 'mdi-react/ChevronUpIcon'
import { Link } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import IkommLogo from '@/assets/img/ikomm/ikomm_control_logo.png'
import KomplettLogo from '@/assets/img/komplett/komplett-logo.png'
import MobitLogo from '@/assets/img/mobit/mobit-logo.png'
import SkytechLogo from '@/assets/img/skytech/logo-black.png'
import TeliaLogo from '@/assets/img/telia/telia_logo_text.svg'
import ThreeStepITLogo from '@/assets/img/threeStepIT/threeStepIT-logo-dark.svg'
import RouterLink from '@/components/RouterLink'
import { useOnClickOutside } from '@/hooks/useOnClickOutside'
import { useEditTranslationsPrivilege, useIsSkytech, useProfile } from '@/hooks/user'
import { commonQueries } from '@/resources/queries'
import { commonSystemMessagesResources } from '@/resources/system-messages'
import { APP_ROUTES } from '@/util/app-routes'
import { getAppName } from '@/util/getAppName'

import SystemMessageList from './SystemMessageList'

import './index.styles.scss'

export const Header: FC = () => {
  const profile = useProfile()
  const translate = useTranslation()
  const editTranslationsPrivilege = useEditTranslationsPrivilege()
  const { locale, whiteLabel, isEditTranslationsMode, setIsEditTranslationsMode } = useAppContext()
  const isSkytech = useIsSkytech()

  const readSystemMessages = localStorage.getItem('read-system-messages')
  const seenMessagedInitial = readSystemMessages ? JSON.parse(readSystemMessages) : undefined

  const [isUserInfoExpanded, setIsUserInfoExpanded] = useState(false)
  const [seenMessages, setSeenMessages] = useState<number[] | undefined>(seenMessagedInitial)
  const menuRef = useRef(null)

  useOnClickOutside(menuRef, () => setIsUserInfoExpanded(false))

  const { data: systemMessages } = useQuery(
    commonQueries.systemMessages(),
    commonSystemMessagesResources.getList
  )

  const localeBasedMessages = systemMessages?.filter(message => message.locale === locale)
  const displayedMessage = localeBasedMessages?.[0]
  const isNotificationRead = displayedMessage ? seenMessages?.includes(displayedMessage?.id) : false
  const appName = getAppName(whiteLabel)
  const isDevelopmentMode = config.ENVIRONMENT === 'dev'
  const environmentBadgeLabel = getEnvironmentBadgeLabel(config.ENVIRONMENT)

  return (
    <header className={whiteLabel}>
      <div className="header-logo">
        <Link to="/">
          {whiteLabel === 'telia' ? (
            <div className="logo-telia-header">
              <Image className="logo-telia" alt={translate('Logo')} src={TeliaLogo} />
              <h1>
                <Translation id="app_header_manager" />
              </h1>
            </div>
          ) : whiteLabel === 'threeStepIT' ? (
            <div className="logo-threeStepIT-header flex">
              <Image className="my-4 ml-4" alt={translate('Logo')} src={ThreeStepITLogo} />
              <h1>
                <Translation id="app_header_manager" />
              </h1>
            </div>
          ) : whiteLabel === 'komplett' ? (
            <div className="logo-komplett-header">
              <Image className="logo-komplett" alt={translate('Logo')} src={KomplettLogo} />
              <h1>
                <Translation id="app_header_manager" />
              </h1>
            </div>
          ) : whiteLabel === 'mobit' ? (
            <div className="logo-mobit-header">
              <Image className="logo-mobit" alt={translate('Logo')} src={MobitLogo} />
            </div>
          ) : whiteLabel === 'ikomm' ? (
            <div className="logo-ikomm-header">
              <Image className="logo-ikomm" alt={translate('Logo')} src={IkommLogo} />
              <h1>
                <Translation id="app_header_manager" />
              </h1>
            </div>
          ) : whiteLabel === 'skytech' ? (
            <div className="logo-skytech-header">
              <div>
                <Image className="-mr-3 ml-1 mt-3 w-28" alt={translate('Logo')} src={SkytechLogo} />
              </div>
              <h1>
                <Translation id="app_header_manager" />
              </h1>
            </div>
          ) : (
            <h1>
              <Translation id={appName} />
            </h1>
          )}
        </Link>

        {environmentBadgeLabel && (
          <div className="development-indication-mobile">{environmentBadgeLabel}</div>
        )}
      </div>

      {isDevelopmentMode && isSkytech && <DevelopmentModeBanner />}

      <div
        ref={menuRef}
        className={`header-user-info ${isUserInfoExpanded ? 'expanded' : 'collapsed'} ${environmentBadgeLabel ? 'is-development-mode' : ''}`}
      >
        <div className="content-wrapper">
          <div className="content">
            <div className="logged-in-as">
              <Translation id="Logged in as" />
            </div>

            <div>
              {profile?.name || <Translation id="Manager" />}
              <div className="email">{profile?.email}</div>
              <div className="phone-number">{profile?.phoneNumber}</div>
            </div>

            {['CAN_EDIT_OWN_LANG', 'CAN_EDIT_ALL_LANG'].includes(editTranslationsPrivilege) && (
              <Toggle
                id="edit-translations-mode"
                label={<Translation id="Edit translations mode" />}
                checked={isEditTranslationsMode}
                marginBottom={16}
                marginTop={16}
                onChange={() => setIsEditTranslationsMode(!isEditTranslationsMode)}
              />
            )}
            <RouterLink to={APP_ROUTES.ACCOUNT_SETTINGS} className="mb-3 text-sm underline">
              <Translation id="Account Settings" />
            </RouterLink>
            <Button size="small" className="m-0" onClick={() => AuthClient.logOut()}>
              <Translation id="Log out" />
            </Button>
          </div>

          <SystemMessageList
            seenMessages={seenMessages}
            onSeenMessagesSet={setSeenMessages}
            onBarClose={() => setIsUserInfoExpanded(false)}
          />
        </div>

        <button
          className="collapse-button"
          onClick={() => setIsUserInfoExpanded(!isUserInfoExpanded)}
        >
          <AccountIcon className="account-icon" />

          <span className="title">
            <span>
              {isUserInfoExpanded ? (
                <Translation id="Close menu" />
              ) : (
                <div className="profile-name">
                  <div> {profile?.name || <Translation id="Manager" />}</div>
                  {environmentBadgeLabel && (
                    <div className="development-indication">{environmentBadgeLabel}</div>
                  )}
                </div>
              )}
            </span>

            <span
              className={`system-message-icon ${
                displayedMessage && !isNotificationRead ? 'is-visible' : ''
              }`}
            >
              <BellIcon />
            </span>
          </span>

          <span className="chevron-container">
            <ChevronUpIcon className="chevron-up" />
          </span>
        </button>
      </div>
    </header>
  )
}

const DevelopmentModeBanner: FC = () => {
  const { locale, whiteLabel, setWhiteLabel, setLocale } = useAppContext()
  return (
    <div
      className={twJoin(
        'mx-2 my-auto hidden flex-row place-content-start items-start md:flex',
        whiteLabel === 'tietoevry' ? 'w-6/12' : 'w-7/12'
      )}
    >
      <div className="border-1 border-red/50 flex gap-2 rounded-lg border-solid p-1">
        <Select
          id="select-white-label"
          className="w-32"
          value={whiteLabel}
          options={WHITE_LABEL_LIST.map(wl => ({ value: wl, label: wl }))}
          onChange={v => {
            if (v !== null) {
              setWhiteLabel(v?.value)
            }
          }}
        />
        <Select
          id="select-locale"
          value={locale}
          options={availableLanguages.map(lang => ({ value: lang, label: lang }))}
          onChange={v => {
            if (v !== null) {
              setLocale(v?.value)
            }
          }}
        />
      </div>
    </div>
  )
}
